import MuiSelect from "@mui/material/Select";
import type { SelectProps as MuiSelectProps } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import type { MenuItemProps } from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import { v4 } from "uuid";
import { get } from "lodash";
import { useEffect } from "react";
import type { ReactNode } from "react";
import { useFormikContext } from "formik";

import { useFormContext } from "@/hooks/useFormContext";
import { FormSkeleton } from "../../skeleton";

type FormValues = Record<string, unknown>;

export interface SelectOption {
  value: unknown;
  label: ReactNode;
  [key: string]: unknown;
}

export interface SelectProps extends Omit<MuiSelectProps<unknown>, "error"> {
  name: string;
  options?: SelectOption[];
  loading?: boolean;
  helperText?: ReactNode;
  renderItem?: (option: SelectOption) => ReactNode;
  error?: string;
}

export function Select({
  id,
  name,
  className,
  options,
  label,
  placeholder,
  color,
  loading,
  variant,
  onChange,
  disabled,
  value,
  renderItem,
  helperText,
  sx,
  error,
  required,
}: SelectProps) {
  const formik = useFormikContext<FormValues>();
  const formContext = useFormContext();
  let labelId: string | undefined;
  let errorLabelId: string | undefined;

  useEffect(() => {
    labelId = v4();
    errorLabelId = v4();
  }, []);

  useEffect(() => {
    formik.setFieldError(name, error);
  }, [error]);

  return (
    <FormSkeleton loading={loading ?? formContext?.loading}>
      <FormControl
        sx={{ width: "100%", textAlign: "left" }}
        error={get(formik?.touched, name) && Boolean(get(formik.errors, name))}
      >
        <InputLabel id={labelId}>{label}</InputLabel>
        <MuiSelect
          id={id}
          name={name}
          className={className}
          labelId={labelId}
          placeholder={placeholder}
          displayEmpty={!!placeholder}
          label={label}
          color={color}
          variant={variant}
          value={value ?? get(formik.values, name, "")}
          onChange={onChange ?? formik.handleChange}
          disabled={disabled}
          sx={sx}
          required={required}
        >
          {placeholder && (
            <MenuItem disabled value="">
              {placeholder}
            </MenuItem>
          )}
          {options?.map((option, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <MenuItem key={index} value={option.value as MenuItemProps["value"]}>
              {renderItem ? renderItem(option) : option.label}
            </MenuItem>
          ))}
        </MuiSelect>
        {get(formik?.touched, name) && Boolean(get(formik.errors, name)) ? (
          <FormHelperText id={errorLabelId}>{get(formik.errors, name) as ReactNode}</FormHelperText>
        ) : (
          <FormHelperText>{helperText}</FormHelperText>
        )}
      </FormControl>
    </FormSkeleton>
  );
}
